import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Space, Spin, Tooltip, Typography, theme } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useT } from '../../../locales';
import {
  emitirVersionRecibo,
  listarVersionesRecibo,
  type ReceiptVersionHistory,
} from '../api/receipt-versions';
import { ReceiptActionButtons, ReceiptPdfViewer, useReceiptActions } from './ReceiptActions';

const { Text } = Typography;

/** M07/M11/M13: actions are explicit, confirmed, and mutually exclusive. */
export function ReceiptVersionPanel({ idPago }: { idPago: number }) {
  const t = useT();
  const { token } = theme.useToken();
  const [history, setHistory] = useState<ReceiptVersionHistory | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const [lookupError, setLookupError] = useState(false);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [issued, setIssued] = useState(false);
  const busyRef = useRef(false);
  const generationRef = useRef(0);
  const aliveRef = useRef(true);
  const selectedVersion = history?.versiones.find((version) => version.numero === selected);
  const actions = useReceiptActions(
    selectedVersion ? { id: idPago, formatoRecibo: 3, version: selectedVersion.numero } : null,
  );
  const busy = issuing || actions.isDownloading || actions.isPrinting;

  const load = useCallback(
    async (preferred?: number) => {
      const generation = ++generationRef.current;
      setLoading(true);
      setLookupError(false);
      try {
        const data = await listarVersionesRecibo(idPago);
        if (!aliveRef.current || generation !== generationRef.current) return;
        setHistory(data);
        setSelected((current) => {
          const desired = preferred ?? current;
          return (
            data.versiones.find((version) => version.numero === desired)?.numero ??
            data.versiones.at(-1)?.numero ??
            null
          );
        });
      } catch {
        if (aliveRef.current && generation === generationRef.current) setLookupError(true);
      } finally {
        if (aliveRef.current && generation === generationRef.current) setLoading(false);
      }
    },
    [idPago],
  );

  useEffect(() => {
    aliveRef.current = true;
    void load();
    return () => {
      aliveRef.current = false;
      generationRef.current += 1;
    };
  }, [load]);

  const issue = async () => {
    if (busyRef.current || busy || loading || !history?.actualizacion.elegible) return;
    busyRef.current = true;
    setIssuing(true);
    setIssueError(null);
    setIssued(false);
    try {
      const result = await emitirVersionRecibo(idPago);
      if (!aliveRef.current) return;
      // Issuance success is authoritative even if refreshing history fails.
      setIssued(true);
      setHistory((previous) =>
        previous
          ? {
              ...previous,
              versiones: [
                ...previous.versiones.filter((version) => version.numero !== result.version.numero),
                result.version,
              ],
              actualizacion: {
                elegible: false,
                codigo: 'SIN_CAMBIOS',
                motivo: t('pages.pagos.receiptVersions.refreshReason'),
              },
            }
          : previous,
      );
      setSelected(result.version.numero);
      await load(result.version.numero);
    } catch (error) {
      if (!aliveRef.current) return;
      const unchanged =
        axios.isAxiosError(error) && error.response?.data?.code === 'RECIBO_SIN_CAMBIOS';
      setIssueError(
        t(
          unchanged
            ? 'pages.pagos.receiptVersions.unchanged'
            : 'pages.pagos.receiptVersions.issueError',
        ),
      );
      if (unchanged) await load();
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setIssuing(false);
    }
  };

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Text>{t('pages.pagos.receiptVersions.explanation')}</Text>
      {history?.estadoPago === 'ANULADO' ? (
        <Alert
          type="warning"
          showIcon
          title={t('pages.pagos.receiptVersions.cancelled')}
          description={t('pages.pagos.receiptVersions.cancelledDescription')}
        />
      ) : null}
      {issued ? (
        <div role="status">
          <Alert type="success" showIcon title={t('pages.pagos.receiptVersions.success')} />
        </div>
      ) : null}
      {issueError ? <Alert type="error" showIcon title={issueError} /> : null}
      {loading ? (
        <div role="status">
          <Spin size="small" /> <Text>{t('pages.pagos.receiptVersions.loading')}</Text>
        </div>
      ) : null}
      {lookupError ? (
        <Alert
          type="error"
          showIcon
          title={t('pages.pagos.receiptVersions.lookupError')}
          action={
            <Button disabled={busy} onClick={() => void load()}>
              {t('pages.pagos.receiptVersions.retry')}
            </Button>
          }
        />
      ) : null}
      {history && !lookupError ? (
        <>
          {history.versiones.length > 0 ? (
            <>
              <label htmlFor={`receipt-version-${idPago}`}>
                <Text strong>{t('pages.pagos.receiptVersions.select')}</Text>
              </label>
              <select
                id={`receipt-version-${idPago}`}
                aria-label={t('pages.pagos.receiptVersions.select')}
                value={selected ?? ''}
                onChange={(event) => {
                  actions.closeViewer();
                  setSelected(Number(event.target.value));
                }}
                disabled={busy || loading}
                style={{
                  width: '100%',
                  minHeight: token.controlHeight,
                  color: token.colorText,
                  background: token.colorBgContainer,
                  border: `${token.lineWidth}px solid ${token.colorBorder}`,
                  borderRadius: token.borderRadius,
                  padding: token.paddingXS,
                }}
              >
                {history.versiones.map((version) => (
                  <option key={version.numero} value={version.numero}>
                    {t('pages.pagos.receiptVersions.option', {
                      version: version.numero,
                      date: new Date(version.capturadoEn).toLocaleString(),
                    })}
                  </option>
                ))}
              </select>
              {selectedVersion?.etiquetaReconstruccion ? (
                <Alert type="info" showIcon title={selectedVersion.etiquetaReconstruccion} />
              ) : null}
              <ReceiptActionButtons
                actions={{
                  ...actions,
                  isDownloading: actions.isDownloading || issuing || loading,
                  isPrinting: actions.isPrinting,
                }}
                downloadLabel={t('pages.pagos.receiptVersions.download')}
                printLabel={t('pages.pagos.receiptVersions.print')}
              />
            </>
          ) : (
            <Text type="secondary">{t('pages.pagos.receiptVersions.empty')}</Text>
          )}
          <Text type="secondary">{history.actualizacion.motivo}</Text>
          <Space wrap>
            <Button disabled={busy || loading} onClick={() => void load()}>
              {t('pages.pagos.receiptVersions.retry')}
            </Button>
            <Button
              type="primary"
              loading={issuing}
              disabled={busy || loading || !history.actualizacion.elegible}
              onClick={() => void issue()}
            >
              {t(
                history.versiones.length === 0
                  ? 'pages.pagos.receiptVersions.issueFirst'
                  : 'pages.pagos.receiptVersions.issueUpdate',
              )}
            </Button>
          </Space>
        </>
      ) : null}
      {actions.viewerUrl ? (
        <ReceiptPdfViewer
          url={actions.viewerUrl}
          title={t('pages.pagos.receiptViewerTitle')}
          fallbackMessage={t('pages.pagos.receiptPrintFallback')}
          closeLabel={t('common.close')}
          onClose={actions.closeViewer}
        />
      ) : null}
    </Space>
  );
}

/** Mount history only after an explicit click; no extra query per table row. */
export function ReceiptVersionsButton({
  idPago,
  compact = false,
}: {
  idPago: number;
  compact?: boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip title={t('pages.pagos.receiptVersions.title')}>
        <Button
          type={compact ? 'text' : 'default'}
          aria-label={t('pages.pagos.receiptVersions.title')}
          icon={<HistoryOutlined aria-hidden />}
          onClick={() => setOpen(true)}
        >
          {compact ? null : t('pages.pagos.receiptVersions.title')}
        </Button>
      </Tooltip>
      <Modal
        open={open}
        title={t('pages.pagos.receiptVersions.title')}
        onCancel={() => setOpen(false)}
        footer={<Button onClick={() => setOpen(false)}>{t('common.close')}</Button>}
        destroyOnHidden
        width={640}
      >
        {open ? <ReceiptVersionPanel key={idPago} idPago={idPago} /> : null}
      </Modal>
    </>
  );
}
